// Globals
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch } from '@/store';
import { createMetaTag, deleteMetaTag, getMetaTag, getMetaTags, updateMetaTag } from '../../feature';

// Components
import { useForm } from '@/components';
import { PlusIcon } from '@/icons';
import { Button, Drawer } from '@/ui';
import { MetaTagsForm } from '../../components';
import MetaTagsList from '../../components/MetaTagsList/MetaTagsList';
import { StyledContentBlock, StyledHeader, StyledPageTitle, StyledPageWrapper } from '../../components/Styled';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Models
import { IMetaTag } from '@/models/metaTags.model';

// Helpers
import { useModal } from '@/hooks';
import { CreateMetaTagsDTO, GetMetaTagsDTO, UpdateMetaTagDTO } from '../../dtos';
import { TMetaTagsFormValues } from '../../helpers/types';
import { useGetMetaTags } from '../../hooks';

const MetaTagsListContainer = () => {
  const { t } = useTranslation('metatags');
  const { canManage } = usePermissions();
  const canManageSeo = canManage(Resource.SEO);
  const { isLoading, redirects, pagination, query } = useGetMetaTags();
  const dispatch = useAppDispatch();
  const [form] = useForm();
  const [editableMetaTag, setEditableMetaTag] = useState<IMetaTag | null>(null);
  const {
    isOpened: isCreateRedirectOpened,
    openModal: openCreateRedirectModal,
    closeModal: closeCreateRedirectModal,
  } = useModal();

  const handleChangePage = (page: number) => {
    const dto = new GetMetaTagsDTO({ page, ...query });
    dispatch(getMetaTags(dto));
  };

  const handleClose = () => {
    setEditableMetaTag(null);
    closeCreateRedirectModal();
  };

  const handleEdit = async (metaTag: IMetaTag) => {
    try {
      const response = await dispatch(
        getMetaTag({
          id: metaTag.id,
        }),
      ).unwrap();

      setEditableMetaTag(response.data);
      openCreateRedirectModal();
    } catch (error) {}
  };

  const handleDelete = async (item: IMetaTag) => {
    try {
      await dispatch(
        deleteMetaTag({
          id: item.id,
        }),
      ).unwrap();
      await dispatch(getMetaTags({ page: 1 }));
    } catch (error) {}
  };

  const handleUpsertMetaTag = async (data: TMetaTagsFormValues) => {
    try {
      if (editableMetaTag?.id) {
        const dto = new UpdateMetaTagDTO({
          id: editableMetaTag.id,
          ...data,
        });
        await dispatch(updateMetaTag(dto)).unwrap();
      } else {
        const dto = new CreateMetaTagsDTO(data);
        await dispatch(createMetaTag(dto)).unwrap();
      }
      await dispatch(getMetaTags({ page: 1 }));
      closeCreateRedirectModal();
      setEditableMetaTag(null);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <StyledPageWrapper>
      <StyledHeader>
        <StyledPageTitle>{t('metatags_title')}</StyledPageTitle>
        {canManageSeo && (
          <Button icon={<PlusIcon />} onClick={openCreateRedirectModal}>
            {t('metatags_add')}
          </Button>
        )}
      </StyledHeader>
      <StyledContentBlock>
        <MetaTagsList
          isLoading={isLoading}
          currentPage={pagination?.current_page ?? 1}
          onPageChange={handleChangePage}
          total={pagination?.total ?? 0}
          list={redirects ?? []}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </StyledContentBlock>
      <Drawer
        open={isCreateRedirectOpened || !!editableMetaTag}
        onClose={handleClose}
        destroyOnClose
        width={700}
        title={t(editableMetaTag ? 'metatags_edit' : 'metatags_add')}
      >
        <MetaTagsForm
          onSubmit={handleUpsertMetaTag}
          isLoading={isLoading}
          initialValues={editableMetaTag}
          formInstance={form}
        />
      </Drawer>
    </StyledPageWrapper>
  );
};

export default MetaTagsListContainer;
